<script lang="ts">
    import hash from "object-hash"
    import { newNotification, removeNotification, login } from "../../store"
    import {
        getActiveOfferTemplate,
        geteOfferTemplatePreview,
        createOfferTemplate,
        updateOfferTemplate,
    } from "../../api"
    import { apiBaseURL } from "../../config"
    import { requireLogin, textareaAutoHeight } from "../../helper"

    import MiniLoader from "../widgets/MiniLoader.svelte"
    import Popover from "../widgets/Popover.svelte"
    import Icon from "../widgets/Icon.svelte"
    import { mdiInformationOutline, mdiFilePdfBox, mdiAlertCircleOutline } from "@mdi/js"
    import Select from "svelte-select"
    import DocumentTemplateTextContent from "./document-template-textContent.svelte"
    import { placeholderHint, imageFormatHint, imageSimpleFormatHint } from "../../config"
    import { _ } from "svelte-i18n"
    requireLogin()

    export let lang = "de"
    export let type: "offer" | "orderConfirmation" = "offer"
    export let title = $_("components.documentTemplate.offerTemplate")
    export let selectedSource: { value: number; label: string } = { value: null, label: null }

    let notification: NotificationEntry
    $: loginData = $login
    $: if (loginData) getOfferTemplateData()

    let initTemplate: DocumentTemplate = {
        id: null,
        active: true,
        type: type,
        source: selectedSource.value,
        dealerNumber: loginData?.account?.ident?.dealerNumber,
        pdfLayout: {
            backgroundImage1: null,
            backgroundImageN: null,
            simpleLayout: false,
            borders: {
                top: null,
                right: null,
                bottom: null,
                left: null,
            },

            logoPosition: {
                verticalAlignment: "top",
                horizontalAlignment: "right",
                verticalOffset: null,
                horizontalOffset: null,
            },
        },
        texts: {
            emailSubject: "",
            emailBody: "",
            pdfIntro: "",
            pdfOuttro: "",
            contact: {
                useLoggedInUserAsDefault: false,
                name: "",
                email: "",
                phone: "",
                webUrl: "",
            },
            deliveryTime: "",
            paymentTerms: "",
        },
    }

    let template = JSON.parse(JSON.stringify(initTemplate))
    let templateHash: string = null
    let inProgress = false
    let previewInProgress = false
    let inProgressPlaceholder = $_("general.beeingLoaded")

    let horizontalAlignment = {
        value: template.pdfLayout.logoPosition.horizontalAlignment,
        label:
            template.pdfLayout.logoPosition.horizontalAlignment == "left"
                ? $_("components.documentTemplate.left")
                : $_("components.documentTemplate.right"),
    }
    let verticalAlignment = {
        value: template.pdfLayout.logoPosition.verticalAlignment,
        label:
            template.pdfLayout.logoPosition.verticalAlignment == "top"
                ? $_("components.documentTemplate.top")
                : $_("components.documentTemplate.bottom"),
    }

    const getOfferTemplateData = async () => {
        const dealerNumber = loginData?.account?.ident?.dealerNumber
        if (!dealerNumber) return

        inProgress = true
        try {
            let templ = await fetchTemplate(dealerNumber, selectedSource.value)
            if (!templ) {
                // get standard
                const _templ = await fetchTemplate(dealerNumber, null)
                if (_templ) {
                    // remove id so not updating existing default template
                    // @ts-ignore
                    _templ.defaultTplId = _templ.id
                    delete _templ.id
                    templ = _templ
                }
            } else {
                // @ts-ignore
                delete templ.defaultTplId
            }
            if (!templ) {
                templ = JSON.parse(JSON.stringify(initTemplate))
            }
            templ.source = selectedSource.value
            if (templ) {
                template = processTemplate(templ)
            }
        } catch (e) {
            console.error(e)
        } finally {
            inProgress = false
        }
    }

    const fetchTemplate = async (dealerNumber: number, source: number | null) => {
        const response = await getActiveOfferTemplate(dealerNumber, type, source)
        return response.data.length ? response.data[0] : null
    }

    const processTemplate = (template: DocumentTemplate) => {
        if (template.pdfLayout.borders && !template.pdfLayout.logoPosition) {
            const { right: verticalOffset, top: horizontalOffset } = template.pdfLayout.borders
            template.pdfLayout.logoPosition = {
                verticalAlignment: "top",
                horizontalAlignment: "right",
                verticalOffset,
                horizontalOffset,
            }
        }

        updateAlignmentVariables(template)

        templateHash = hash(template)
        return template
    }

    const updateAlignmentVariables = (template: DocumentTemplate) => {
        horizontalAlignment = {
            value: template.pdfLayout.logoPosition.horizontalAlignment,
            label:
                template.pdfLayout.logoPosition.horizontalAlignment === "left"
                    ? $_("components.documentTemplate.left")
                    : $_("components.documentTemplate.right"),
        }
        verticalAlignment = {
            value: template.pdfLayout.logoPosition.verticalAlignment,
            label:
                template.pdfLayout.logoPosition.verticalAlignment === "top"
                    ? $_("components.documentTemplate.top")
                    : $_("components.documentTemplate.bottom"),
        }
    }
    const getPreview = async () => {
        if (!template.dealerNumber) {
            template.dealerNumber = loginData?.account?.ident?.dealerNumber
        }

        previewInProgress = true

        geteOfferTemplatePreview(template)
            .then((response) => {
                const file = response.data
                const src = URL.createObjectURL(file)
                window.open(src)
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {
                previewInProgress = false
            })
    }

    const updateTemplate = async (e: Event) => {
        removeNotification(notification)
        inProgress = true

        const tpl = await getActiveOfferTemplate(loginData?.account?.ident?.dealerNumber, type, selectedSource.value)

        if (template.id || tpl?.data?.length) {
            template.id = tpl?.data?.[0]?.id || template?.id

            updateOfferTemplate(template, type)
                .then(() => {
                    templateHash = hash(template)
                    notification = newNotification({
                        class: "success",
                        html: $_("notifications.changesGotSaved"),
                        timeout: 3000,
                        removeOnNewLocation: true,
                    })
                })
                .catch((e) => {
                    console.error(e)
                })
                .finally(() => {
                    inProgress = false
                })
        } else {
            template.dealerNumber = loginData?.account?.ident?.dealerNumber

            createOfferTemplate(template)
                .then(() => {
                    templateHash = hash(template)
                    notification = newNotification({
                        class: "success",
                        html: $_("components.documentTemplate.offerTemplateGotSaved"),
                        timeout: 3000,
                        removeOnNewLocation: true,
                    })
                })
                .catch((e) => {
                    console.error(e)
                })
                .finally(() => {
                    inProgress = false
                })
        }
    }

    const onChangeFileUpload = (e: Event) => {
        let input = e.target as HTMLInputElement
        let file = input.files[0]
        if (!checkFile(file)) return
        if (file) {
            const reader = new FileReader()
            reader.addEventListener("load", function () {
                template.pdfLayout[(e.target as HTMLInputElement).name] = {
                    path: file.name,
                    type: file.type,
                    size: file.size,
                    lastModified: file.lastModified,
                    src: reader.result,
                }
            })
            reader.readAsDataURL(file)
        }
    }

    $: {
        if (horizontalAlignment?.value) {
            template.pdfLayout.logoPosition.horizontalAlignment = horizontalAlignment.value
        }
    }
    $: {
        if (verticalAlignment?.value) {
            template.pdfLayout.logoPosition.verticalAlignment = verticalAlignment.value
        }
    }

    $: fileBase = apiBaseURL + "document-template/" + (template.defaultTplId || template.id)

    function checkFile(file: File) {
        if (
            file.type.toLowerCase() !== "image/jpeg" &&
            file.type.toLowerCase() !== "image/jpg" &&
            file.type.toLowerCase() !== "image/png"
        ) {
            newNotification({
                html: $_("components.documentTemplate.filesAllowed"),
                class: "error",
            })
            return false
        }
        return true
    }
</script>

<section data-lang="{lang}">
    <div class="container">
        <h1>{title}</h1>
        <div style="display: flex; justify-content: space-between;">
            <div class="layout justify-content-end">
                {#if selectedSource.value}
                    <button
                        type="submit"
                        class="btn btn-secondary btn-outline"
                        on:click="{async () => {
                            let defTemplate = await fetchTemplate($login?.tokenData?.ident?.dealerNumber, null)

                            if (defTemplate) {
                                defTemplate.source = template.source
                                // @ts-ignore
                                defTemplate.defaultTplId = defTemplate.id
                                defTemplate.id = template.id
                                template = processTemplate(defTemplate)
                            } else {
                                template = JSON.parse(JSON.stringify(initTemplate))
                            }
                        }}"
                    >
                        {$_("components.documentTemplate.resetToDefault")}
                    </button>
                {/if}
            </div>
            {#if template.id && templateHash === hash(template)}
                <div class="layout justify-content-end">
                    <button
                        type="submit"
                        class="btn btn-secondary btn-outline"
                        disabled="{previewInProgress}"
                        on:click="{getPreview}"
                    >
                        {#if previewInProgress}
                            <MiniLoader />
                        {:else}
                            <Icon path="{mdiFilePdfBox}" size="24px" />
                            {$_("general.preview")}
                        {/if}
                    </button>
                </div>
            {/if}
        </div>

        <form on:submit|preventDefault="{updateTemplate}">
            <section>
                <div class="section-title no-space">
                    <span> {$_("components.documentTemplate.letterPaper")}</span>
                </div>
                <div class="row">
                    <div class="col-sm-12" style="display: block; padding-top: 20px;">
                        <input
                            type="radio"
                            id="layoutViaBackground"
                            bind:group="{template.pdfLayout.simpleLayout}"
                            value="{false}"
                        /><label for="layoutViaBackground" style="display: inline"
                            >{$_("components.documentTemplate.defineLayoutThroughBGImages")}</label
                        ><br />
                        <input
                            type="radio"
                            id="layoutViaLogo"
                            bind:group="{template.pdfLayout.simpleLayout}"
                            value="{true}"
                        /><label for="layoutViaLogo" style="display: inline"
                            >{$_("components.documentTemplate.defineLayoutThroughLogo")}</label
                        ><br />
                    </div>
                </div>

                {#if template.pdfLayout.simpleLayout}
                    <div class="row">
                        <div class="col-sm-6 mt-md">
                            {#if imageSimpleFormatHint}
                                <label for="" class="layout layout-gap-xs">
                                    <span>{$_("general.logo")}</span>
                                    <Popover>
                                        <div class="pdfIntroInfoPopupTrigger" slot="trigger">
                                            <Icon
                                                path="{mdiInformationOutline}"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                            />
                                        </div>
                                        <div>{@html imageSimpleFormatHint}</div>
                                    </Popover>
                                </label>{/if}
                            <div>
                                {#if inProgress}
                                    <div class="mb-xs">{inProgressPlaceholder}</div>
                                {:else if template.pdfLayout.logoImage}
                                    {#if template.pdfLayout.logoImage.src.includes("data:image")}
                                        <img
                                            class="bordered"
                                            src="{template.pdfLayout.logoImage.src}"
                                            alt="{template.pdfLayout.logoImage.path}"
                                        />
                                    {:else}
                                        <img
                                            class="bordered mb-sm"
                                            src="{fileBase}/{template.pdfLayout.logoImage.src}?cacheBuster={Date.now()}"
                                            alt="{template.pdfLayout.logoImage.path}"
                                        />
                                    {/if}
                                    <div class="logo-alignment">
                                        <div class="section-title">
                                            {$_("components.documentTemplate.logoPosition")}
                                        </div>
                                        <div class="layout layout-wrap">
                                            <div class="layout layout-wrap layout-gap-md">
                                                <div class="inputholder haslabel has-append no-input-margin">
                                                    <label for="">
                                                        {$_("components.documentTemplate.verticalOrientation")}
                                                    </label>
                                                    <Select
                                                        clearable="{false}"
                                                        showChevron
                                                        bind:value="{verticalAlignment}"
                                                        items="{[
                                                            {
                                                                value: 'top',
                                                                label: $_('components.documentTemplate.top'),
                                                            },
                                                            {
                                                                value: 'bottom',
                                                                label: $_('components.documentTemplate.bottom'),
                                                            },
                                                        ]}"
                                                    />
                                                </div>

                                                <div class="inputholder haslabel has-append no-input-margin">
                                                    <label for="">
                                                        {$_("components.documentTemplate.horizontalOrientation")}
                                                    </label>

                                                    <Select
                                                        clearable="{false}"
                                                        showChevron
                                                        bind:value="{horizontalAlignment}"
                                                        items="{[
                                                            {
                                                                value: 'right',
                                                                label: $_('components.documentTemplate.right'),
                                                            },
                                                            {
                                                                value: 'left',
                                                                label: $_('components.documentTemplate.left'),
                                                            },
                                                        ]}"
                                                    />
                                                </div>
                                            </div>
                                            <div class="layout layout-wrap layout-gap-md">
                                                <div class="inputholder haslabel has-append no-input-margin">
                                                    <label for="">
                                                        {$_("components.documentTemplate.verticalDisplacement")}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        pattern="[0-9]"
                                                        min="0"
                                                        placeholder="0"
                                                        bind:value="{template.pdfLayout.logoPosition.verticalOffset}"
                                                        disabled="{inProgress}"
                                                        id="left-margin"
                                                    />
                                                    <div class="input-append">mm</div>
                                                </div>
                                                <div class="inputholder haslabel has-append no-input-margin">
                                                    <label for="">
                                                        {$_(
                                                            "components.documentTemplate.horizontalDisplacement"
                                                        )}</label
                                                    >
                                                    <input
                                                        type="number"
                                                        step="1"
                                                        pattern="[0-9]"
                                                        min="0"
                                                        placeholder="0"
                                                        bind:value="{template.pdfLayout.logoPosition.horizontalOffset}"
                                                        disabled="{inProgress}"
                                                        id="bottom-margin"
                                                    />
                                                    <div class="input-append">mm</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <br />
                                {:else}
                                    <div class="mb-md alert alert-warning">
                                        <Icon path="{mdiAlertCircleOutline}" />
                                        {$_("components.documentTemplate.noLogoAvailable")}
                                    </div>
                                {/if}

                                {#if !inProgress}
                                    <br />
                                    <label for="logoImage" class="file-input btn btn-tertiary">
                                        {$_("general.selectFile")}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            name="logoImage"
                                            id="logoImage"
                                            data-cy="logo-input"
                                            on:change="{onChangeFileUpload}"
                                            disabled="{inProgress}"
                                        />
                                    </label>

                                    {#if template.pdfLayout.logoImage}
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                                        <label
                                            for=""
                                            class="file-input btn btn-tertiary"
                                            on:click="{() => {
                                                template.pdfLayout.logoImage = null
                                            }}">{$_("components.documentTemplate.deleteFile")}</label
                                        >
                                    {/if}
                                {/if}
                            </div>
                        </div>
                        <div class="col-sm-6 mt-md">
                            {#if imageSimpleFormatHint}
                                <label for="" class="layout layout-gap-xs">
                                    <span> {$_("components.documentTemplate.footer")}</span>
                                    <Popover>
                                        <div class="pdfIntroInfoPopupTrigger" slot="trigger">
                                            <Icon
                                                path="{mdiInformationOutline}"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                            />
                                        </div>
                                        <div>{@html imageSimpleFormatHint}</div>
                                    </Popover>
                                </label>{/if}
                            <div>
                                {#if inProgress}
                                    <div class="mb-xs">{inProgressPlaceholder}</div>
                                {:else if template.pdfLayout.footerImage}
                                    {#if template.pdfLayout.footerImage.src.includes("data:image")}
                                        <img
                                            class="bordered"
                                            src="{template.pdfLayout.footerImage.src}"
                                            alt="{template.pdfLayout.footerImage.path}"
                                        />
                                    {:else}
                                        <img
                                            class="bordered mb-sm"
                                            src="{fileBase}/{template.pdfLayout.footerImage
                                                .src}?cacheBuster={Date.now()}"
                                            alt="{template.pdfLayout.footerImage.path}"
                                        />
                                    {/if}
                                {:else}
                                    <div class="mb-md alert alert-warning">
                                        <Icon path="{mdiAlertCircleOutline}" />
                                        {$_("components.documentTemplate.noFooterAvailable")}
                                    </div>
                                {/if}

                                {#if !inProgress}
                                    <br />
                                    <label for="footerImage" class="file-input btn btn-tertiary">
                                        {$_("general.selectFile")}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            name="footerImage"
                                            id="footerImage"
                                            on:change="{onChangeFileUpload}"
                                            disabled="{inProgress}"
                                        />
                                    </label>

                                    {#if template.pdfLayout.footerImage}
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                                        <label
                                            for=""
                                            class="file-input btn btn-tertiary"
                                            on:click="{() => {
                                                template.pdfLayout.footerImage = null
                                            }}"
                                        >
                                            {$_("components.documentTemplate.deleteFile")}
                                        </label>
                                    {/if}
                                {/if}
                            </div>
                        </div>
                        {#if imageSimpleFormatHint}
                            <div class="hint hint-space-pageimages layout layout-gap-xs mb-sm">
                                <Icon path="{mdiInformationOutline}" width="24" height="24" viewBox="0 0 24 24" />
                                <div>{@html imageSimpleFormatHint}</div>
                            </div>{/if}
                    </div>
                {:else}
                    <div class="row">
                        <div class="col-sm-6 mt-md">
                            {#if imageFormatHint}
                                <label for="" class="layout layout-gap-xs">
                                    <span>{$_("components.documentTemplate.firstPage")}</span>
                                    <Popover>
                                        <div class="pdfIntroInfoPopupTrigger" slot="trigger">
                                            <Icon
                                                path="{mdiInformationOutline}"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                            />
                                        </div>
                                        <div>{@html imageFormatHint}</div>
                                    </Popover>
                                </label>{/if}
                            <div>
                                {#if inProgress}
                                    <div class="mb-xs">{inProgressPlaceholder}</div>
                                {:else if template.pdfLayout.backgroundImage1}
                                    {#if template.pdfLayout.backgroundImage1.src.includes("data:image")}
                                        <img
                                            class="bordered"
                                            src="{template.pdfLayout.backgroundImage1.src}"
                                            alt="{template.pdfLayout.backgroundImage1.path}"
                                        />
                                    {:else}
                                        <img
                                            class="bordered mb-sm"
                                            src="{fileBase}/{template.pdfLayout.backgroundImage1
                                                .src}?cacheBuster={Date.now()}"
                                            alt="{template.pdfLayout.backgroundImage1.path}"
                                        />
                                    {/if}
                                {:else}
                                    <div class="mb-md alert alert-warning">
                                        <Icon path="{mdiAlertCircleOutline}" />
                                        {$_("components.documentTemplate.noTemplateAvailable")}
                                    </div>
                                {/if}

                                {#if !inProgress}
                                    <br />
                                    <label for="firstPage" class="file-input btn btn-tertiary">
                                        {$_("general.selectFile")}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            name="backgroundImage1"
                                            id="firstPage"
                                            on:change="{onChangeFileUpload}"
                                            disabled="{inProgress}"
                                        />
                                    </label>

                                    {#if template.pdfLayout.backgroundImage1}
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                                        <label
                                            for=""
                                            class="file-input btn btn-tertiary"
                                            on:click="{() => {
                                                template.pdfLayout.backgroundImage1 = null
                                            }}"
                                        >
                                            {$_("components.documentTemplate.deleteFile")}
                                        </label>
                                    {/if}
                                {/if}
                            </div>
                        </div>
                        <div class="col-sm-6 mt-md">
                            {#if imageFormatHint}
                                <label for="" class="layout layout-gap-xs">
                                    <span>
                                        {$_("components.documentTemplate.subsequentPages")}
                                    </span>
                                    <Popover>
                                        <div class="pdfIntroInfoPopupTrigger" slot="trigger">
                                            <Icon
                                                path="{mdiInformationOutline}"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                            />
                                        </div>
                                        <div>{@html imageFormatHint}</div>
                                    </Popover>
                                </label>{/if}
                            <div>
                                {#if inProgress}
                                    <div class="mb-xs">{inProgressPlaceholder}</div>
                                {:else if template.pdfLayout.backgroundImageN}
                                    {#if template.pdfLayout.backgroundImageN.src.includes("data:image")}
                                        <img
                                            class="bordered"
                                            src="{template.pdfLayout.backgroundImageN.src}"
                                            alt="{template.pdfLayout.backgroundImageN.path}"
                                        />
                                    {:else}
                                        <img
                                            class="bordered mb-sm"
                                            src="{fileBase}/{template.pdfLayout.backgroundImageN
                                                .src}?cacheBuster={Date.now()}"
                                            alt="{template.pdfLayout.backgroundImageN.path}"
                                        />
                                    {/if}
                                {:else}
                                    <div class="mb-md alert alert-warning">
                                        <Icon path="{mdiAlertCircleOutline}" />
                                        {$_("components.documentTemplate.noTemplateAvailable")}
                                    </div>
                                {/if}

                                {#if !inProgress}
                                    <br />
                                    <label for="followingPages" class="file-input btn btn-tertiary">
                                        {$_("general.selectFile")}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            name="backgroundImageN"
                                            id="followingPages"
                                            on:change="{onChangeFileUpload}"
                                            disabled="{inProgress}"
                                        />
                                    </label>

                                    {#if template.pdfLayout.backgroundImageN}
                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                                        <label
                                            for=""
                                            class="file-input btn btn-tertiary"
                                            on:click="{() => {
                                                template.pdfLayout.backgroundImageN = null
                                            }}"
                                        >
                                            {$_("components.documentTemplate.deleteFile")}
                                        </label>
                                    {/if}
                                {/if}
                            </div>
                        </div>
                        {#if imageFormatHint}
                            <div class="hint hint-space-pageimages layout layout-gap-xs mb-sm">
                                <Icon path="{mdiInformationOutline}" width="24" height="24" viewBox="0 0 24 24" />
                                <div>{@html imageFormatHint}</div>
                            </div>
                        {/if}
                    </div>
                {/if}
            </section>
            <DocumentTemplateTextContent
                bind:texts="{template.texts}"
                bind:inProgress
                bind:inProgressPlaceholder
                {placeholderHint}
                {textareaAutoHeight}
                {type}
            />
            <section>
                <div class="section-title">{$_("components.documentTemplate.margins")}</div>
                <div class="layout layout-wrap layout-gap-md">
                    <div class="inputholder haslabel has-append no-input-margin">
                        <label for=""
                            >{$_("components.documentTemplate.top")}
                            <small>({$_("components.documentTemplate.forSubsequentPages")})</small></label
                        >
                        <input
                            type="number"
                            step="1"
                            pattern="[0-9]"
                            min="0"
                            placeholder="0"
                            bind:value="{template.pdfLayout.borders.top}"
                            disabled="{inProgress}"
                            id="top-margin"
                        />
                        <div class="input-append">mm</div>
                    </div>

                    <div class="inputholder haslabel has-append no-input-margin">
                        <label for="">{$_("components.documentTemplate.right")}</label>
                        <input
                            type="number"
                            step="1"
                            pattern="[0-9]"
                            min="0"
                            placeholder="0"
                            bind:value="{template.pdfLayout.borders.right}"
                            disabled="{inProgress}"
                            id="right-margin"
                        />
                        <div class="input-append">mm</div>
                    </div>

                    <div class="inputholder haslabel has-append no-input-margin">
                        <label for="">{$_("components.documentTemplate.bottom")}</label>
                        <input
                            type="number"
                            step="1"
                            pattern="[0-9]"
                            min="0"
                            placeholder="0"
                            bind:value="{template.pdfLayout.borders.bottom}"
                            disabled="{inProgress}"
                            id="bottom-margin"
                        />
                        <div class="input-append">mm</div>
                    </div>

                    <div class="inputholder haslabel has-append no-input-margin">
                        <label for="">{$_("components.documentTemplate.left")}</label>
                        <input
                            type="number"
                            step="1"
                            pattern="[0-9]"
                            min="0"
                            placeholder="0"
                            bind:value="{template.pdfLayout.borders.left}"
                            disabled="{inProgress}"
                            id="left-margin"
                        />
                        <div class="input-append">mm</div>
                    </div>
                </div>
            </section>

            <section class="layout justify-content-end layout-gap-md mt-md mb-xl">
                {#if template.id && templateHash === hash(template)}
                    <button
                        type="submit"
                        class="btn btn-secondary btn-outline"
                        disabled="{previewInProgress}"
                        on:click="{getPreview}"
                        id="preview"
                    >
                        {#if previewInProgress}
                            <MiniLoader />
                        {:else}
                            <Icon path="{mdiFilePdfBox}" size="24px" />
                            {$_("components.documentTemplate.preview")}
                        {/if}
                    </button>
                {/if}
                <button type="submit" class="btn" id="save" disabled="{inProgress}">{$_("general.save")}</button>
            </section>
        </form>
        <div class="legend">
            <div class="headline">{$_("general.legend")}</div>
            <div class="items">
                <section>
                    <div class="item">
                        <div class="icon">*</div>
                        <div class="text">{$_("components.documentTemplate.mandatoryField")}</div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</section>
